import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, AlertTriangle, MapPin } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";

const COUNTRY_CODES = ["+254", "+44", "+1", "+33", "+49", "+234", "+27", "+255", "+256", "+91"];
const MAX_BYTES = 2 * 1024 * 1024;
const OK_TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function Contact() {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", cc: "+254", phone: "", message: "" });
  const [file, setFile] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (!OK_TYPES.includes(f.type)) { toast.error("Please attach a JPEG, PNG or WEBP image"); e.target.value = ""; return; }
    if (f.size > MAX_BYTES) { toast.error("Image must be 2MB or smaller"); e.target.value = ""; return; }
    setFile(f);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email and a message"); return;
    }
    toast.success("Thanks — your message is on its way. We reply to every message, usually within a day.");
    setForm({ name: "", email: "", cc: "+254", phone: "", message: "" });
    setFile(null);
  };

  const Channel = ({ icon: Icon, title, lines, testid }) => (
    <div className="k-container" data-testid={testid} style={{ padding: 16 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Icon size={16} color="var(--accent)" />
        <div className="k-heading" style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
      </div>
      {lines.map((l, i) => (
        <p key={i} className="k-muted" style={{ fontSize: 13, margin: "6px 0 0" }}>{l}</p>
      ))}
    </div>
  );

  return (
    <div className="k-canvas" data-testid="contact-page">
      <PublicNav />
      <section style={{ margin: "0 auto", padding: "56px 24px 60px" }}>
        <h1 className="k-heading" data-testid="contact-h1"
          style={{ fontSize: "clamp(36px,5vw,56px)", letterSpacing: "-.03em", margin: 0 }}>
          We reply to every message. Usually within a day.
        </h1>
        <p className="k-muted" data-testid="contact-sub" style={{ fontSize: 18, marginTop: 16, lineHeight: 1.5 }}>
          Questions about your build, billing, or an enterprise or agency account?
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 14, marginTop: 26 }}>
          <Channel icon={Mail} title="Email" testid="contact-ch-email"
            lines={[<a key="e" className="k-link" data-testid="contact-email" href="mailto:info@kadana.ai">info@kadana.ai</a>,
              "Fastest for account, billing, and enterprise-onboarding queries. Response within 24h."]} />
          <Channel icon={Phone} title="Phone" testid="contact-ch-phone"
            lines={["+254 758 004 013", "Best-effort — voicemail after hours. Escalations only."]} />
          <Channel icon={MessageCircle} title="WhatsApp" testid="contact-ch-whatsapp"
            lines={["+254 758 004 013", "Chat with the operator team. Voice notes welcome."]} />
          <Channel icon={AlertTriangle} title="Urgent matters" testid="contact-ch-urgent"
            lines={[<span key="u">For security concerns or press enquiries, email <a className="k-link" href="mailto:info@kadana.ai?subject=URGENT">info@kadana.ai</a> with “URGENT” in the subject line.</span>,
              "On-call operator response within 2 hours."]} />
        </div>

        <div className="k-container" data-testid="contact-faq-link" style={{ padding: 16, marginTop: 14 }}>
          <p className="k-muted" style={{ fontSize: 14, margin: 0 }}>
            About 80% of questions are answered on our{" "}
            <a className="k-link" data-testid="contact-faq" onClick={() => nav("/faq")} style={{ cursor: "pointer" }}>FAQ page</a>
            {" "}— worth a look before you write.
          </p>
        </div>

        <form onSubmit={submit} data-testid="contact-form" style={{ marginTop: 26, maxWidth: 620 }}>
          <div className="k-heading" style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Send us a message</div>
          <label className="k-muted" style={{ fontSize: 13 }}>Full name
            <input className="k-control" data-testid="contact-name" value={form.name} onChange={set("name")} style={{ marginTop: 4 }} />
          </label>
          <label className="k-muted" style={{ fontSize: 13, display: "block", marginTop: 12 }}>Email address
            <input className="k-control" data-testid="contact-form-email" type="email" value={form.email} onChange={set("email")} style={{ marginTop: 4 }} />
          </label>
          <label className="k-muted" style={{ fontSize: 13, display: "block", marginTop: 12 }}>Phone number
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <select className="k-control" data-testid="contact-cc" value={form.cc} onChange={set("cc")} style={{ width: 100 }}>
                {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input className="k-control" data-testid="contact-phone" value={form.phone} onChange={set("phone")} style={{ flex: 1 }} />
            </div>
          </label>
          <label className="k-muted" style={{ fontSize: 13, display: "block", marginTop: 12 }}>Message
            <textarea className="k-control" data-testid="contact-message" rows={5} value={form.message} onChange={set("message")} style={{ marginTop: 4 }} />
          </label>
          <label className="k-muted" style={{ fontSize: 13, display: "block", marginTop: 12 }}>Image attachment (max 2MB — JPEG, PNG, WEBP)
            <input className="k-control" data-testid="contact-file" type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} style={{ marginTop: 4 }} />
          </label>
          {file ? <p className="k-muted" style={{ fontSize: 12, marginTop: 6 }}>Attached: {file.name}</p> : null}
          <button className="k-btn k-btn-primary" data-testid="contact-submit" type="submit" style={{ marginTop: 16 }}>Send message</button>
        </form>

        <div data-testid="contact-address" style={{ marginTop: 30, display: "flex", gap: 8, alignItems: "flex-start" }}>
          <MapPin size={16} color="var(--muted)" style={{ flex: "none", marginTop: 3 }} />
          <p className="k-muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.6 }}>
            Melian Dialogue (Kenya) Limited, P.O. Box B1003, Urban Oasis Apartments, Kingara Road,
            Lavington, Nairobi, Republic of Kenya.
          </p>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
